import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { UserPlus } from 'lucide-react';
import { accessApi, type UserListItem } from '@/api/access-api';
import { usePermission } from '@/auth/use-permission';
import { ACCESS_MANAGE_USERS } from '@loanaudit/shared';

import { DataTable, type Column } from '@/acc-ui/primitives/DataTable';
import { Badge } from '@/acc-ui/primitives/Badge';
import { Button } from '@/acc-ui/primitives/Button';
import { Input } from '@/acc-ui/primitives/Input';
import { SearchInput } from '@/acc-ui/primitives/SearchInput';
import { HighlightText } from '@/acc-ui/primitives/HighlightText';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { Dialog } from '@/acc-ui/primitives/Dialog';
import { Sheet } from '@/acc-ui/primitives/Sheet';
import { ErrorState } from '@/acc-ui/primitives/Layout';

export function AccessUsersTab() {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [search, setSearch] = useState('');
  const { has } = usePermission();
  const canManage = has(ACCESS_MANAGE_USERS);

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<UserListItem | null>(null);

  const debouncedSearch = useDebouncedValue(search.trim(), 300);
  const usersQuery = useQuery({
    queryKey: ['users', { page, pageSize, search: debouncedSearch }],
    queryFn: () => accessApi.listUsers({ page, pageSize, search: debouncedSearch || undefined }),
    placeholderData: (prev) => prev,
  });

  const rolesQuery = useQuery({ queryKey: ['roles'], queryFn: accessApi.listRoles });

  const columns: Column<UserListItem>[] = useMemo(
    () => [
      {
        key: 'displayName',
        header: 'User',
        cell: (u) => (
          <div className="flex flex-col">
            <span className="font-medium text-text-primary">
              <HighlightText text={u.displayName} query={debouncedSearch} />
            </span>
            <span className="text-caption text-text-tertiary">
              <HighlightText text={u.email} query={debouncedSearch} />
            </span>
          </div>
        ),
      },
      {
        key: 'roles',
        header: 'Roles',
        cell: (u) => (
          <div className="flex flex-wrap gap-1">
            {u.isSuperAdmin && <Badge tone="info" dot>Super Admin</Badge>}
            {u.roles.length === 0 && !u.isSuperAdmin && (
              <span className="text-text-muted text-caption italic">No roles</span>
            )}
            {u.roles.map((r) => (
              <Badge key={r} tone="muted">{r}</Badge>
            ))}
          </div>
        ),
      },
      {
        key: 'isActive',
        header: 'Status',
        cell: (u) =>
          u.isActive ? <Badge tone="success" dot>Active</Badge> : <Badge tone="neutral" dot>Inactive</Badge>,
      },
      {
        key: 'lastLoginAt',
        header: 'Last sign-in',
        cell: (u) =>
          u.lastLoginAt ? (
            <span className="text-caption text-text-secondary tabular-nums">
              {new Date(u.lastLoginAt).toLocaleString()}
            </span>
          ) : (
            <span className="text-text-muted text-caption">—</span>
          ),
      },
    ],
    [debouncedSearch],
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex-1 max-w-md">
          <SearchInput
            placeholder="Search users by name or email…"
            value={search}
            onChange={(v) => {
              setSearch(v);
              setPage(1);
            }}
          />
        </div>
        {canManage && (
          <Button
            leftIcon={<UserPlus className="w-4 h-4" />}
            onClick={() => setCreateOpen(true)}
          >
            Add user
          </Button>
        )}
      </div>

      {usersQuery.isError ? (
        <ErrorState
          description="Could not load users."
          action={<Button onClick={() => usersQuery.refetch()}>Retry</Button>}
        />
      ) : (
        <DataTable<UserListItem>
          columns={columns}
          rows={usersQuery.data?.items ?? []}
          rowKey={(u) => u.id}
          page={page}
          pageSize={pageSize}
          total={usersQuery.data?.total ?? 0}
          onPageChange={setPage}
          loading={usersQuery.isLoading}
          onRowClick={canManage ? setEditing : undefined}
          emptyMessage={
            debouncedSearch ? (
              <div className="flex flex-col items-center gap-2">
                <span>No users match “<span className="font-medium text-text-secondary">{debouncedSearch}</span>”.</span>
                <span className="text-caption text-text-tertiary">Try a partial name or email.</span>
                <Button variant="secondary" size="sm" onClick={() => { setSearch(''); setPage(1); }}>Clear search</Button>
              </div>
            ) : (
              'No users yet. Once teammates sign in with O365, they will appear here.'
            )
          }
        />
      )}

      {canManage && (
        <CreateUserDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          roles={rolesQuery.data ?? []}
          onCreated={() => {
            setCreateOpen(false);
            qc.invalidateQueries({ queryKey: ['users'] });
          }}
        />
      )}

      {canManage && editing && (
        <EditUserDrawer
          user={editing}
          roles={rolesQuery.data ?? []}
          onOpenChange={(open) => !open && setEditing(null)}
          onSaved={() => {
            setEditing(null);
            qc.invalidateQueries({ queryKey: ['users'] });
          }}
        />
      )}
    </div>
  );
}

// ---------------- Create dialog ----------------

function CreateUserDialog({
  open,
  onOpenChange,
  roles,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  roles: { key: string; name: string; isBuiltin: boolean }[];
  onCreated: () => void;
}) {
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [selectedRoles, setSelectedRoles] = useState<Set<string>>(new Set());
  const [err, setErr] = useState<string | null>(null);

  const reset = () => {
    setEmail('');
    setDisplayName('');
    setSelectedRoles(new Set());
    setErr(null);
  };

  const toggle = (key: string) => {
    const next = new Set(selectedRoles);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setSelectedRoles(next);
  };

  const mut = useMutation({
    mutationFn: () =>
      accessApi.createUser({
        email: email.trim().toLowerCase(),
        displayName: displayName.trim(),
        roles: Array.from(selectedRoles),
        directPermissions: [],
      }),
    onSuccess: () => {
      reset();
      onCreated();
    },
    onError: (e) => setErr(e instanceof Error ? e.message : 'Could not create user'),
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) reset();
        onOpenChange(v);
      }}
      title="Add user"
      description="Users sign in with Office 365 — the email must match their O365 sign-in exactly. This entry becomes active on first sign-in."
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              setErr(null);
              if (!email.trim() || !displayName.trim()) {
                setErr('Email and display name are required');
                return;
              }
              mut.mutate();
            }}
            loading={mut.isPending}
          >
            Create user
          </Button>
        </>
      }
    >
      <label className="flex flex-col gap-1">
        <span className="text-caption font-semibold text-text-secondary">Email (O365) <span className="text-red-700">*</span></span>
        <Input
          type="email"
          placeholder="firstname.lastname@axisfinance.in"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </label>
      <label className="flex flex-col gap-1">
        <span className="text-caption font-semibold text-text-secondary">Display name <span className="text-red-700">*</span></span>
        <Input
          placeholder="e.g. Priya Menon"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
        />
      </label>
      <div className="flex flex-col gap-1.5">
        <span className="text-caption font-semibold text-text-secondary">Roles</span>
        <div className="flex flex-wrap gap-1.5">
          {roles.length === 0 && (
            <span className="text-text-tertiary text-caption">
              No roles yet. Create roles first or leave this user with no access.
            </span>
          )}
          {roles.map((r) => {
            const on = selectedRoles.has(r.key);
            return (
              <button
                key={r.key}
                type="button"
                onClick={() => toggle(r.key)}
                className={
                  'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-caption font-medium border transition-colors ' +
                  (on
                    ? 'bg-brand-blue/10 border-brand-blue/30 text-brand-blue'
                    : 'bg-white border-glass-hairline text-text-secondary hover:bg-slate-900/5')
                }
              >
                {r.name}
                {r.isBuiltin && <span className="text-micro text-text-muted">(builtin)</span>}
              </button>
            );
          })}
        </div>
      </div>
      {err && (
        <div className="text-caption text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          {err}
        </div>
      )}
    </Dialog>
  );
}

// ---------------- Edit drawer ----------------

function EditUserDrawer({
  user,
  roles,
  onOpenChange,
  onSaved,
}: {
  user: UserListItem;
  roles: { key: string; name: string; isBuiltin: boolean }[];
  onOpenChange: (v: boolean) => void;
  onSaved: () => void;
}) {
  const [displayName, setDisplayName] = useState(user.displayName);
  const [isActive, setIsActive] = useState(user.isActive);
  const [selectedRoles, setSelectedRoles] = useState<Set<string>>(new Set(user.roles));
  const [err, setErr] = useState<string | null>(null);

  const toggle = (key: string) => {
    const next = new Set(selectedRoles);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setSelectedRoles(next);
  };

  const mut = useMutation({
    mutationFn: () =>
      accessApi.updateUser(user.id, {
        displayName: displayName.trim(),
        isActive,
        roles: Array.from(selectedRoles),
      }),
    onSuccess: onSaved,
    onError: (e) => setErr(e instanceof Error ? e.message : 'Could not update user'),
  });

  return (
    <Sheet open onOpenChange={onOpenChange} side="right" title={user.displayName} widthPx={420}>
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between gap-3 p-3 bg-white/60 rounded-glass-sm border border-glass-hairline">
          <div className="min-w-0">
            <div className="text-label font-semibold text-text-primary">{user.email}</div>
            <div className="text-micro text-text-tertiary">
              {user.isSuperAdmin ? 'Super Admin (bypasses all checks)' : 'Standard user'}
            </div>
          </div>
          <Badge tone={user.isActive ? 'success' : 'neutral'} dot>
            {user.isActive ? 'Active' : 'Inactive'}
          </Badge>
        </div>

        <label className="flex flex-col gap-1">
          <span className="text-caption font-semibold text-text-secondary">Display name <span className="text-red-700">*</span></span>
          <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
        </label>

        <label className="flex items-center gap-2 text-label text-text-secondary">
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            className="w-4 h-4 accent-brand-blue"
          />
          <span>Account active</span>
        </label>

        <div className="flex flex-col gap-1.5">
          <span className="text-caption font-semibold text-text-secondary">Roles</span>
          <div className="flex flex-wrap gap-1.5">
            {roles.map((r) => {
              const on = selectedRoles.has(r.key);
              return (
                <button
                  key={r.key}
                  type="button"
                  onClick={() => toggle(r.key)}
                  className={
                    'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-caption font-medium border transition-colors ' +
                    (on
                      ? 'bg-brand-blue/10 border-brand-blue/30 text-brand-blue'
                      : 'bg-white border-glass-hairline text-text-secondary hover:bg-slate-900/5')
                  }
                >
                  {r.name}
                </button>
              );
            })}
          </div>
        </div>

        {err && (
          <div className="text-caption text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
            {err}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={() => mut.mutate()} loading={mut.isPending}>
            Save changes
          </Button>
        </div>
      </div>
    </Sheet>
  );
}
